<script setup>
import { ref, watch, computed } from "vue";
import useDebug from "@/hooks/useDebug";
import { useToast } from "vue-toastification";
import { yearMonthDayFormat } from "@/utils/dateUtils";

const props = defineProps({
  modelValue: Boolean,
  selectedIds: {
    type: Array,
    default: () => [],
  },
  mode: {
    type: String,
    default: "",
    validator: (v) => ["", "lot", "expiry"].includes(v),
  },
});
const emit = defineEmits(["update:modelValue", "updated"]);

const {batchUpdateLots, batchUpdateExpiry } = useDebug();
const toast = useToast();

const dialog = ref(props.modelValue);
const saving = ref(false);
const form = ref({
  lot: "",
  expiry: "",
  reason: "",
});

watch(
  () => props.modelValue,
  (v) => {
    dialog.value = v;
    if (v) {
      form.value = { lot: "", expiry: "", reason: "" };
    }
  }
);

watch(dialog, (v) => emit("update:modelValue", v));

const canSave = computed(() => {
  if (!form.value.reason) return false;
  if (props.mode === "lot") return !!form.value.lot;
  if (props.mode === "expiry") return !!form.value.expiry;
  return !!form.value.lot || !!form.value.expiry;
});

const save = async () => {
  if (props.selectedIds.length === 0) {
    toast.error("No LPs selected.");
    return;
  }
  if (!canSave.value) return;

  saving.value = true;
  try {
    const params = { 
      edit_reason: form.value.reason, 
    };

    if (props.mode === "lot") {
      params.lot_code = form.value.lot;
      await batchUpdateLots(props.selectedIds, params);
    } 
    else if (props.mode === "expiry") {
      const formatted = yearMonthDayFormat(form.value.expiry, "-");
      if (!formatted) {
        toast.error("Invalid date format.");
        return;
      }
      params.expiry_date = formatted;
      await batchUpdateExpiry(props.selectedIds, params);
    }

    emit("updated");
    dialog.value = false;
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <v-dialog v-model="dialog" max-width="600" persistent>
    <v-card>
      <v-card-title>Batch update LPs</v-card-title>
      <v-card-text>
        <div class="d-flex ga-4 mb-4">
          <v-text-field
            v-if="props.mode !== 'expiry'"
            v-model="form.lot"
            label="Lot"
            variant="underlined"
            hide-details="auto"
            flat
          />
          <v-text-field
            v-if="props.mode !== 'lot'"
            v-model="form.expiry"
            label="Expiry"
            placeholder="MM-DD-YYYY"
            variant="underlined"
            hide-details="auto"
            flat
          />
        </div>
        <v-text-field
          v-model="form.reason"
          class="mb-4"
          label="Reason for Edit"
          variant="underlined"
          hide-details="auto"
        />
        <small>{{ selectedIds.length }} LPNs selected</small>
      </v-card-text>
      <template v-slot:actions>
        <v-btn color="red" class="ml-auto" variant="text" @click="dialog = false">
          Cancel
        </v-btn>
        <v-btn
          color="green"
          variant="text"
          @click="save"
          :disabled="!canSave"
          :loading="saving"
        >
          Save
        </v-btn>
      </template>
    </v-card>
  </v-dialog>
</template>
